import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "./AuthContext";
import { permissionAllowed } from "../lib/businessPermissions";
import { clearAdminImpersonation, getAdminImpersonation, type AdminImpersonationSession } from "../lib/adminImpersonation";

const STORAGE_KEY = "iscan.activeBusinessId";

export interface ActiveBusiness {
  id: string;
  name: string;
  role: string;
  permissions?: Record<string, boolean>;
}

interface BusinessContextType {
  memberships: ActiveBusiness[];
  activeBusiness: ActiveBusiness | null;
  loading: boolean;
  error: string | null;
  permissions: Record<string, boolean>;
  impersonation: AdminImpersonationSession | null;
  isReadOnlyImpersonation: boolean;
  hasPermission: (permission: string) => boolean;
  refreshMemberships: () => Promise<void>;
  setActiveBusiness: (business: ActiveBusiness) => void;
  clearActiveBusiness: () => void;
}

const BusinessContext = createContext<BusinessContextType>({
  memberships: [],
  activeBusiness: null,
  loading: true,
  error: null,
  permissions: {},
  impersonation: null,
  isReadOnlyImpersonation: false,
  hasPermission: () => true,
  refreshMemberships: async () => {},
  setActiveBusiness: () => {},
  clearActiveBusiness: () => {},
});

interface MembershipJoinRow {
  role: string;
  businesses: { id: string; name: string; status?: string | null } | null;
}

interface MyBusinessRow {
  business_id: string;
  business_name: string;
  role: string;
}

function isMissingBusinessStatusError(error: { code?: string; message?: string } | null | undefined) {
  const message = String(error?.message || "");
  return (
    error?.code === "42703" ||
    error?.code === "PGRST204" ||
    /businesses.*status/i.test(message) ||
    /status.*businesses/i.test(message) ||
    /column.*status/i.test(message)
  );
}

function toActiveBusiness(row: MembershipJoinRow): ActiveBusiness | null {
  if (!row.businesses) return null;
  if (row.businesses.status === "suspended") return null;
  return {
    id: row.businesses.id,
    name: row.businesses.name,
    role: row.role,
  };
}

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [memberships, setMemberships] = useState<ActiveBusiness[]>([]);
  const [activeBusiness, setActiveBusinessState] = useState<ActiveBusiness | null>(null);
  const activeBusinessIdRef = useRef<string | null>(null);
  // Use a ref for user so refreshMemberships doesn't need it as a reactive dep.
  // This prevents TOKEN_REFRESHED events (new object, same user id) from re-triggering the effect.
  const userRef = useRef(user);
  userRef.current = user;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<AdminImpersonationSession | null>(null);

  async function loadPermissions(business: ActiveBusiness | null): Promise<Record<string, boolean>> {
    const currentUser = userRef.current;
    if (!currentUser || !business || business.role === "owner") return {};
    const { data, error: permissionError } = await supabase
      .from("business_member_permissions")
      .select("permission_key, allowed")
      .eq("business_id", business.id)
      .eq("user_id", currentUser.id);
    if (permissionError) return {};
    return Object.fromEntries(((data as { permission_key: string; allowed: boolean }[]) ?? []).map((row) => [row.permission_key, row.allowed]));
  }

  const clearActiveBusiness = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    activeBusinessIdRef.current = null;
    setActiveBusinessState(null);
  }, []);

  const setActiveBusiness = useCallback((business: ActiveBusiness) => {
    localStorage.setItem(STORAGE_KEY, business.id);
    activeBusinessIdRef.current = business.id;
    setActiveBusinessState(business);
  }, []);

  const refreshMemberships = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser) {
      setMemberships([]);
      setError(null);
      setImpersonation(null);
      clearActiveBusiness();
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const activeImpersonation = getAdminImpersonation();
    if (activeImpersonation) {
      const next = activeImpersonation.businesses;
      setImpersonation(activeImpersonation);
      setMemberships(next);

      const storedId = localStorage.getItem(STORAGE_KEY);
      const stored = next.find((business) => business.id === storedId);
      const current = next.find((business) => business.id === activeBusinessIdRef.current);
      const fallback = stored ?? current ?? next[0] ?? null;

      if (fallback) {
        localStorage.setItem(STORAGE_KEY, fallback.id);
        activeBusinessIdRef.current = fallback.id;
        setActiveBusinessState(fallback);
      } else {
        clearAdminImpersonation();
        clearActiveBusiness();
      }
      setLoading(false);
      return;
    }

    setImpersonation(null);

    const { data: rpcData, error: rpcError } = await supabase.rpc("get_my_businesses");

    let next: ActiveBusiness[] = [];

    if (!rpcError) {
      next = ((rpcData as MyBusinessRow[]) ?? []).map((row) => ({
        id: row.business_id,
        name: row.business_name,
        role: row.role,
      }));
    } else {
      let { data, error: fallbackError } = await supabase
        .from("memberships")
        .select("role, businesses(id, name, status)")
        .eq("user_id", currentUser.id)
        .order("joined_at", { ascending: true });

      if (fallbackError && isMissingBusinessStatusError(fallbackError)) {
        const fallback = await supabase
          .from("memberships")
          .select("role, businesses(id, name)")
          .eq("user_id", currentUser.id)
          .order("joined_at", { ascending: true });
        data = fallback.data as unknown as typeof data;
        fallbackError = fallback.error;
      }

      if (fallbackError) {
        setMemberships([]);
        setError(`${rpcError.message} ${fallbackError.message}`);
        clearActiveBusiness();
        setLoading(false);
        return;
      }

      next = ((data as unknown as MembershipJoinRow[]) ?? [])
        .map(toActiveBusiness)
        .filter((business): business is ActiveBusiness => business !== null);

      if (next.length === 0) {
        setError(rpcError.message);
      }
    }

    setMemberships(next);

    const storedId = localStorage.getItem(STORAGE_KEY);
    const stored = next.find((business) => business.id === storedId);
    const current = next.find((business) => business.id === activeBusinessIdRef.current);
    const fallback = stored ?? current ?? next[0] ?? null;

    if (fallback) {
      localStorage.setItem(STORAGE_KEY, fallback.id);
      activeBusinessIdRef.current = fallback.id;
      setActiveBusinessState({ ...fallback, permissions: await loadPermissions(fallback) });
    } else {
      clearActiveBusiness();
    }

    setLoading(false);
  }, [clearActiveBusiness]); // no `user` dep — read via userRef to avoid TOKEN_REFRESHED re-triggers

  // Run only when the actual user changes (sign in / sign out / different account),
  // not on every TOKEN_REFRESHED event that creates a new user object reference.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (authLoading) return;
    refreshMemberships();
  }, [authLoading, userId]);

  useEffect(() => {
    let mounted = true;
    if (impersonation) return;
    if (!user || !activeBusiness || activeBusiness.role === "owner") return;
    loadPermissions(activeBusiness).then((permissions) => {
      if (!mounted) return;
      setActiveBusinessState((current) => (
        current?.id === activeBusiness.id ? { ...current, permissions } : current
      ));
    });
    return () => { mounted = false; };
  }, [activeBusiness?.id, activeBusiness?.role, userId, impersonation]);

  const value = useMemo(
    () => {
      const permissions = activeBusiness?.permissions ?? {};
      const readOnlyPermissions: Record<string, boolean> = {
        dashboard: true,
        invoices: true,
        suppliers: true,
        par_level: true,
        par_check: true,
        par_template: true,
        reports: true,
        scan: false,
        integrations: false,
        team: false,
        business_settings: false,
        owner_summary: false,
      };
      const hasPermission = (permission: string) => {
        if (!activeBusiness) return false;
        if (impersonation) return readOnlyPermissions[permission] === true;
        return permissionAllowed(activeBusiness.role, permission, permissions);
      };
      return {
        memberships,
        activeBusiness,
        loading: authLoading || loading,
        error,
        permissions,
        impersonation,
        isReadOnlyImpersonation: Boolean(impersonation),
        hasPermission,
        refreshMemberships,
        setActiveBusiness,
        clearActiveBusiness,
      };
    },
    [
      memberships,
      activeBusiness,
      authLoading,
      loading,
      error,
      impersonation,
      refreshMemberships,
      setActiveBusiness,
      clearActiveBusiness,
    ],
  );

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  return useContext(BusinessContext);
}
